"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { getBridge, useBridge } from "@/src/lib/bridge";
import { ContainerBroadcastEvent, MessageType } from "@/src/lib/bridge/types";
import { sleep } from "@utils/sleep";
import { isChatTabInShellPath } from "@utils/bridge/shellTabPathPolicy";
import {
  isNativeTabFrozen,
  NATIVE_TAB_FREEZE_CHANGE_EVENT,
  NativeTabFreezeChangeDetail,
} from "./useNativeTabFreezeSync";
import { usePathname } from "./usePathname";
import {
  isOnboardingRoute,
  isWelcomeRoute,
} from "./onboarding/isOnboardingRoute";

/** container-broadcast event：某 WebView 已 claim 并展示指定 modal */
const GLOBAL_APP_MODAL_CLAIMED_EVENT =
  ContainerBroadcastEvent.GLOBAL_APP_MODAL_CLAIMED;

type GlobalAppModalClaimedPayload = {
  modalKey?: string;
};

export type UseGlobalAppModalLimitOptions = {
  /**
   * 其它 WebView 已通过 container-broadcast claim 同一 modal 时调用。
   * 用于关闭本实例已打开但用户当时不可见的遗留弹窗。
   */
  onRemoteClaim?: () => void;
  /**
   * 为 true 时，App 内处于 `/app-chat-modal` 壳 Tab 路由下始终不展示
   *（`canShow` 为 false，`tryOpen` / `scheduleOpen` 不执行）。
   */
  hideInAppChatModal?: boolean;
};

/**
 * App 多 WebView 下，当前实例是否具备展示全局 modal 的前置条件：
 * - 非 App：恒 true（单页）
 * - App：Tab 未 freeze 且 document 为 visible
 */
export function isAppModalShowEligible(): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }
  if (!getBridge().isInApp()) {
    return true;
  }
  if (isNativeTabFrozen()) {
    return false;
  }
  return document.visibilityState === "visible";
}

/**
 * 等到当前 WebView 具备展示条件（visible 且未 freeze）后 resolve。
 *
 * 供 `*ModalCallable` 这类非 React 入口使用：在 App 多 WebView 下，不可见 / freeze 的容器里弹出的
 * 弹窗会残留到下次复用该 WebView 时才被用户看到（表现为「刚进页面就莫名弹窗」）。hook 版
 * {@link useGlobalAppModalLimit} 只能在组件内用，这里给出等价的一次性等待。
 *
 * @param signal 调用方取消（如页面卸载）；已取消或取消时 resolve `false`，调用方应放弃弹窗
 * @returns 是否应继续展示
 */
export function waitForAppModalEligible(
  signal?: AbortSignal,
): Promise<boolean> {
  if (signal?.aborted) {
    return Promise.resolve(false);
  }
  if (isAppModalShowEligible()) {
    return Promise.resolve(true);
  }
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(false);
  }

  return new Promise<boolean>((resolve) => {
    const cleanup = () => {
      document.removeEventListener("visibilitychange", onMaybeEligible);
      window.removeEventListener(
        NATIVE_TAB_FREEZE_CHANGE_EVENT,
        onMaybeEligible,
      );
      signal?.removeEventListener("abort", onAbort);
    };

    function onMaybeEligible() {
      if (!isAppModalShowEligible()) {
        return;
      }
      cleanup();
      resolve(true);
    }

    function onAbort() {
      cleanup();
      resolve(false);
    }

    document.addEventListener("visibilitychange", onMaybeEligible);
    window.addEventListener(NATIVE_TAB_FREEZE_CHANGE_EVENT, onMaybeEligible);
    signal?.addEventListener("abort", onAbort);
  });
}

/**
 * App 端全局 modal 多 WebView 互斥控制。
 *
 * 解决多 Tab 壳下各 WebView 独立运行、localStorage 竞态导致同一弹窗叠弹的问题：
 * 1. 仅 visible 且未 freeze 的 WebView 可展示
 * 2. 展示成功后 container-broadcast，其它 WebView 本会话 suppress + 关闭已开弹窗
 * 3. freeze / 新一轮尝试时自动取消进行中的延迟打开
 *
 * @param modalKey 弹窗唯一标识，如 `'turn-on-notification'`
 * @param options.onRemoteClaim 收到其它 WebView claim 广播时关闭本实例弹窗
 *
 * @example
 * ```tsx
 * const { canShow, scheduleOpen } = useGlobalAppModalLimit("turn-on-notification", {
 *   onRemoteClaim: () => setVisible(false),
 * });
 *
 * useEffect(() => {
 *   if (!businessReady || !canShow) return;
 *   return scheduleOpen(4000, () => setVisible(true));
 * }, [businessReady, canShow, scheduleOpen]);
 * ```
 */
export function useGlobalAppModalLimit(
  modalKey: string,
  options?: UseGlobalAppModalLimitOptions,
) {
  const { isInApp, on } = useBridge();
  const hideInAppChatModal = options?.hideInAppChatModal ?? false;
  const pathname = usePathname();
  const hiddenOnAppChatModal =
    hideInAppChatModal && isInApp && isChatTabInShellPath(pathname);
  // 隐藏 onboarding 和 welcome 页面的 modal
  const hiddenOnOnboarding =
    isInApp && (isOnboardingRoute(pathname) || isWelcomeRoute(pathname));
  const [eligible, setEligible] = useState(() => isAppModalShowEligible());
  const [suppressed, setSuppressed] = useState(false);
  const openAttemptGenerationRef = useRef(0);
  const suppressedRef = useRef(false);
  const onRemoteClaimRef = useRef(options?.onRemoteClaim);
  onRemoteClaimRef.current = options?.onRemoteClaim;

  // 隐藏弹窗
  const hiddenModal = hiddenOnAppChatModal || hiddenOnOnboarding;

  const refreshEligible = useCallback(() => {
    setEligible(isAppModalShowEligible());
  }, []);

  useEffect(() => {
    if (!isInApp) {
      return;
    }

    const onFreezeChange = (event: Event) => {
      const { frozen } = (event as CustomEvent<NativeTabFreezeChangeDetail>)
        .detail;
      if (frozen) {
        openAttemptGenerationRef.current += 1;
      }
      refreshEligible();
    };

    const onVisibilityChange = () => {
      refreshEligible();
    };

    window.addEventListener(NATIVE_TAB_FREEZE_CHANGE_EVENT, onFreezeChange);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener(
        NATIVE_TAB_FREEZE_CHANGE_EVENT,
        onFreezeChange,
      );
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isInApp, refreshEligible]);

  useEffect(() => {
    if (!isInApp || !on) {
      return;
    }

    const handleBroadcast = (
      data: GlobalAppModalClaimedPayload & {
        event?: string;
        payload?: GlobalAppModalClaimedPayload;
      },
    ) => {
      if (data?.event !== GLOBAL_APP_MODAL_CLAIMED_EVENT) {
        return;
      }
      const key = data.modalKey ?? data.payload?.modalKey;
      if (key !== modalKey) {
        return;
      }
      suppressedRef.current = true;
      setSuppressed(true);
      openAttemptGenerationRef.current += 1;
      onRemoteClaimRef.current?.();
    };

    const unsubscribe = on(MessageType.CONTAINER_BROADCAST, handleBroadcast);
    return unsubscribe;
  }, [isInApp, on, modalKey]);

  useEffect(() => {
    if (hiddenModal) {
      openAttemptGenerationRef.current += 1;
    }
  }, [hiddenModal]);

  const canShow = !isInApp || (eligible && !suppressed && !hiddenModal);

  const notifyClaimed = useCallback(() => {
    if (!isInApp) {
      return;
    }
    getBridge().broadcastGlobalAppModalClaimed({ modalKey });
  }, [isInApp, modalKey]);

  const tryOpen = useCallback(
    (open: () => void): boolean => {
      if (isInApp && !isAppModalShowEligible()) {
        return false;
      }
      if (isInApp && suppressedRef.current) {
        return false;
      }
      if (hiddenModal) {
        return false;
      }
      open();
      if (isInApp) {
        notifyClaimed();
      }
      return true;
    },
    [hiddenModal, isInApp, modalKey, notifyClaimed],
  );

  const scheduleOpen = useCallback(
    (delayMs: number, open: () => void): (() => void) => {
      const attemptId = openAttemptGenerationRef.current + 1;
      openAttemptGenerationRef.current = attemptId;

      let cancelled = false;

      const run = async () => {
        await sleep(delayMs);
        if (cancelled || attemptId !== openAttemptGenerationRef.current) {
          return;
        }
        tryOpen(open);
      };

      void run();

      return () => {
        cancelled = true;
        if (attemptId === openAttemptGenerationRef.current) {
          openAttemptGenerationRef.current += 1;
        }
      };
    },
    [modalKey, tryOpen],
  );

  const cancelPendingOpen = useCallback(() => {
    openAttemptGenerationRef.current += 1;
  }, []);

  return {
    /** 当前 WebView 是否允许展示（App: visible + 未 freeze + 未被 broadcast 抑制 + 非 Chat 壳路由屏蔽） */
    canShow,
    /** 是否已被其它 WebView 的 container-broadcast 抑制（本会话） */
    isSuppressed: suppressed,
    /** 立即尝试打开；成功时 broadcast 并返回 true */
    tryOpen,
    /** 延迟打开；freeze / 新 attempt / unmount 时自动取消；返回 cleanup */
    scheduleOpen,
    /** 手动广播已展示（open 在 tryOpen 外执行时使用） */
    notifyClaimed,
    /** 主动取消进行中的 scheduleOpen */
    cancelPendingOpen,
  };
}
